import type { ConversationEmber, ConversationMessage } from './api';

/**
 * The thread as a window on the conversation (2026-09-25 拍板, §4.5). A person's
 * one conversation never ends, so the page never holds all of it by default:
 * it opens on the newest turns, reaches back when the reader scrolls up, and
 * its poll asks only for what came after the last turn it holds. These are
 * the two pure moves every read lands through.
 */

/** How many turns the page opens on, and reads back at a time — the default; the value in force is clientConfig().thread_window (client-config.ts, 2026-10-19 Riley). */
export const THREAD_WINDOW = 40;

/**
 * Fold turns the edge sent into the turns the page holds: one entry per
 * turn_index, the edge's copy winning (it is the truth about a turn), in
 * thread order. Turns the page composed itself (an optimistic send, a reply
 * still being written) have indexes of their own and survive untouched
 * unless the edge has that index too. Returns `held` itself when nothing
 * changed, so a poll that brought nothing new re-renders nothing.
 */
export function mergeTurns(held: ConversationMessage[], incoming: ConversationMessage[]): ConversationMessage[] {
  if (incoming.length === 0) return held;
  const byIndex = new Map<number, ConversationMessage>();
  for (const m of held) byIndex.set(m.turn_index, m);
  let changed = false;
  for (const m of incoming) {
    const have = byIndex.get(m.turn_index);
    if (have && sameTurn(have, m)) continue;
    byIndex.set(m.turn_index, m);
    changed = true;
  }
  if (!changed) return held;
  return [...byIndex.values()].sort((a, b) => a.turn_index - b.turn_index);
}

function sameTurn(a: ConversationMessage, b: ConversationMessage): boolean {
  return a.role === b.role && a.content === b.content && a.face === b.face && a.source === b.source
    && (a.attachments?.length ?? 0) === (b.attachments?.length ?? 0) && a.letter_id === b.letter_id;
}

/**
 * What the thread draws of an Ember card, in one string: that it exists,
 * whether it has been presented, and whether it is whole yet. A card whose
 * signature has not moved is not redrawn.
 */
export function emberSignature(e: ConversationEmber): string {
  return `${e.id}:${e.presented_at ?? ''}:${e.media.header_image_pending ? 1 : 0}${e.media.music_pending ? 1 : 0}`;
}

/**
 * Fold the Embers a read carried into the ones the page holds: an Ember is
 * read with the turns it stands among (a window's, a forward read's, and
 * every one still arriving), so a read brings some of them, never a
 * statement about the rest. One entry per id, the read's copy winning when
 * its card moved, in the order they were written. Returns `held` itself
 * when nothing changed. Nothing is ever dropped here: an Ember deleted
 * elsewhere leaves the thread on the next open.
 */
export function mergeEmbers(held: ConversationEmber[], incoming: ConversationEmber[]): ConversationEmber[] {
  if (incoming.length === 0) return held;
  const byId = new Map<string, ConversationEmber>();
  for (const e of held) byId.set(e.id, e);
  let changed = false;
  for (const e of incoming) {
    const have = byId.get(e.id);
    if (have && emberSignature(have) === emberSignature(e)) continue;
    byId.set(e.id, e);
    changed = true;
  }
  if (!changed) return held;
  return [...byId.values()].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
}
